import { FieldStatus } from "../../types/FieldStatuses";
import { DocFieldConfig } from "../config/DocFieldConfig";
import { AllowEmptyDlfMatcher } from "../matchers/AllowEmptyDlfMatcher";
import { DlfMatcher, DlfMatcherProperties } from "../matchers/DlfMatcher";
import { MatcherFactory } from "../matchers/factory/MatcherFactory";
import { RegexFieldMatcher } from "../matchers/RegexFieldMatcher";
import { ValidationType } from "../validation/ValidationType";
import { Coordinate, ExtractedCoordinate } from "./Coordinate";

type BaseFieldData = {
  fieldName: string;
  fieldValue: string;
  fieldStatus: FieldStatus;
  /** Average OCR Confidence. Default -1 */
  ocrConfidence: number;
  confidence: number;
  transformed: boolean;
  validationTypes: ValidationType[];
  pageId: string;
  spanIds: Array<string>;
  matcherData: DlfMatcherProperties;
  _requireExtract?: boolean;
} & ExtractedCoordinate;

class BaseField implements ExtractedCoordinate {
  /**
   * This property should be unique between Matchers. This allows us to effectively cache the
   */
  _matcherId?: string;
  _matcherData?: DlfMatcherProperties;
  fieldName: string;
  fieldValue: string;
  fieldStatus: FieldStatus;
  confidence: number;
  /**
   * Ocr Confidence of extraced field. If value is -1, OCR confidence could not be calculated.
   */
  ocrConfidence: number;
  transformed: boolean;
  _validationErrors: Set<ValidationType>;
  pageId: string;
  spanIds: Array<string>;
  /**
   * Indicates that this requires extraction !
   */
  _requireExtract: boolean;
  topLeft?: Coordinate;
  bottomRight?: Coordinate;

  constructor(data: BaseFieldData) {
    this.fieldName = data.fieldName;
    this.fieldValue = data.fieldValue;
    this.fieldStatus = data.fieldStatus;
    this.confidence = data.confidence;
    this.ocrConfidence = data.ocrConfidence ?? -1;

    this.transformed = data.transformed;
    this._validationErrors = new Set(data.validationTypes);

    this.topLeft = data.topLeft;
    this.bottomRight = data.bottomRight;

    this.pageId = data.pageId;
    this.spanIds = data.spanIds;

    this._matcherData = data.matcherData;
    this._requireExtract = data._requireExtract ?? this._matcherData == null;
  }

  get validationTypes(): ValidationType[] {
    return Array.from(this._validationErrors.values());
  }
  get valid(): boolean {
    return (
      this._validationErrors.size === 0 ||
      !this._validationErrors.has("VALIDATION_ERROR")
    );
  }
  get hasOcrWarning(): boolean {
    return this._validationErrors.has("OCR_WARNING");
  }
  set hasOcrWarning(value: boolean) {
    if (!value) {
      this._validationErrors.delete("OCR_WARNING");
    } else {
      this._validationErrors.add("OCR_WARNING");
    }
  }

  get matcherData(): DlfMatcherProperties {
    if (!this._matcherData) {
      // throw new Error('BaseField constructed will NULL matcherData');
      this._matcherData = MatcherFactory.defaultMatcherFrom(
        new DocFieldConfig({
          description: "",
          displayTab: "",
          fieldName: this.fieldName,
          hidden: false,
          applyReuse: false,
          generateReuse: false,
          position: 0,
          validator: {
            clientValidationOperator: "OR",
            validationRules: [],
            fieldName: this.fieldName,
          },
          transformationRules: [],
          threshold: 80, // set threshold to 80 - this shouldn't impact any real Matchers though, so no harm.
          ocrConfidenceThreshold: 0, // set to 0, shouldn't impact real matchers
        }),
      );
    }
    return this._matcherData;
  }

  get matcherId(): string {
    if (!this._matcherId) {
      this._matcherId = MatcherFactory.createDlfMatcher(
        this.matcherData,
      ).getMatcherId();
    }
    return this._matcherId;
  }

  /**
   * Method returns true if the BaseField was extracted by the provided Matcher.
   */
  extractedBy(input?: DlfMatcher): boolean {
    return this.extractedById(input?.getMatcherId());
  }
  extractedById(inputMatcherId?: string): boolean {
    if (inputMatcherId == undefined) return false;
    return this.matcherId === inputMatcherId;
  }

  updateFieldStatusFromAllowEmpty(dlfMatcher: DlfMatcher) {
    // Ensure AllowEmpty from dlf matcher sets correct status on DLF.
    if (
      dlfMatcher.supportsAllowEmpty() &&
      // swap EMPTY status with ERROR if changed
      (this.fieldStatus === "EMPTY" ||
        // swap ERROR status with EMPTY if changed and no OCRR warning
        (this.fieldStatus === "ERROR" && !this.hasOcrWarning))
    ) {
      this.fieldStatus = (dlfMatcher as AllowEmptyDlfMatcher).getAllowEmpty()
        ? "EMPTY"
        : "ERROR";
    }
  }

  public static createTest(rest: Partial<BaseFieldData> = {}): BaseField {
    const defaultBaseField: BaseFieldData = {
      matcherData: RegexFieldMatcher.createTest().toMatcherData(),
      fieldName: "",
      fieldValue: "",
      pageId: "",
      fieldStatus: "NO_STATUS",
      confidence: 0,
      ocrConfidence: -1,
      transformed: false,
      validationTypes: [],
      spanIds: [],
    };

    return new BaseField({
      ...defaultBaseField,
      ...rest,
    });
  }

  /**
   * Returns whether two BaseFields are equal. If both BaseFields are undefined, false is returned
   */
  public static equals(
    a: BaseField | undefined,
    b: BaseField | undefined,
  ): boolean {
    if (!a || !b) return false;

    return (
      a.matcherId.length === b.matcherId.length &&
      a.matcherId === b.matcherId &&
      a.spanIds.length === b.spanIds.length &&
      a.spanIds.every((value) => b.spanIds.includes(value))
    );
  }
}

export { BaseField, BaseFieldData };
